import { gql, useMutation, useQuery } from '@apollo/client';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';

const SERVICE_CONSUMABLES_QUERY = gql`
  query ServiceConsumables($service_id: ID!) {
    serviceConsumables(service_id: $service_id) {
      id
      service_id
      product_id
      quantity
    }
  }
`;

const SERVICE_PRODUCTS_LOOKUP = gql`
  query ServiceProductsLookup {
    products(limit: 200, status_filter: ACTIVE) {
      items { id code name }
    }
  }
`;

const ADD_SERVICE_CONSUMABLE = gql`
  mutation AddServiceConsumable($input: AddServiceConsumableInput!) {
    addServiceConsumable(input: $input) { id }
  }
`;

const UPDATE_SERVICE_CONSUMABLE = gql`
  mutation UpdateServiceConsumable($id: ID!, $input: UpdateServiceConsumableInput!) {
    updateServiceConsumable(id: $id, input: $input) { id }
  }
`;

const REMOVE_SERVICE_CONSUMABLE = gql`
  mutation RemoveServiceConsumable($id: ID!) {
    removeServiceConsumable(id: $id)
  }
`;

interface ConsumableRow {
  id: string;
  service_id: string;
  product_id: string;
  quantity: string;
}

interface ServiceConsumablesResponse {
  serviceConsumables: ConsumableRow[];
}

interface ProductsLookupResponse {
  products: { items: { id: string; code: string; name: string }[] };
}

interface ServiceConsumablesEditorProps {
  service_id: string;
  service_name: string;
  onClose: () => void;
}

const DECIMAL3_RE = /^\d+(\.\d{1,3})?$/;

/**
 * Stage A — inline editor for `service_consumables`. Each service can list the
 * products it consumes per unit; backend `expandLineToConsumptions` multiplies by
 * sale-line qty.
 */
export function ServiceConsumablesEditor({
  service_id,
  service_name,
  onClose,
}: ServiceConsumablesEditorProps): JSX.Element {
  const { t } = useTranslation();
  const { data, refetch } = useQuery<ServiceConsumablesResponse>(SERVICE_CONSUMABLES_QUERY, {
    variables: { service_id },
    fetchPolicy: 'cache-and-network',
  });
  const { data: lookups } = useQuery<ProductsLookupResponse>(SERVICE_PRODUCTS_LOOKUP, {
    fetchPolicy: 'cache-first',
  });
  const [addItem, { loading: adding }] = useMutation(ADD_SERVICE_CONSUMABLE);
  const [updateItem] = useMutation(UPDATE_SERVICE_CONSUMABLE);
  const [removeItem] = useMutation(REMOVE_SERVICE_CONSUMABLE);

  const rows = data?.serviceConsumables ?? [];
  const products = lookups?.products.items ?? [];

  const [draft_id, setDraftId] = useState<string>('');
  const [draft_qty, setDraftQty] = useState<string>('1');
  const [error, setError] = useState<string | null>(null);

  const onAdd = async (): Promise<void> => {
    setError(null);
    if (!draft_id) {
      setError(t('service_consumables.error_pick_product'));
      return;
    }
    if (!DECIMAL3_RE.test(draft_qty)) {
      setError(t('service_consumables.error_invalid_qty'));
      return;
    }
    try {
      await addItem({ variables: { input: { service_id, product_id: draft_id, quantity: draft_qty } } });
      setDraftId('');
      setDraftQty('1');
      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'add_failed';
      setError(
        message.includes('consumable_already_exists')
          ? t('service_consumables.error_duplicate')
          : message,
      );
    }
  };

  const onChangeQty = async (id: string, qty: string): Promise<void> => {
    if (!DECIMAL3_RE.test(qty)) return;
    await updateItem({ variables: { id, input: { quantity: qty } } });
    await refetch();
  };

  const onRemove = async (id: string): Promise<void> => {
    await removeItem({ variables: { id } });
    await refetch();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="max-h-[90vh] w-full max-w-2xl overflow-auto">
        <CardHeader>
          <CardTitle>{t('service_consumables.title', { name: service_name })}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('service_consumables.subtitle')}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t('service_consumables.product')}</th>
                  <th className="px-3 py-2 text-right">{t('service_consumables.quantity')}</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-4 text-center text-muted-foreground">
                      {t('service_consumables.empty')}
                    </td>
                  </tr>
                ) : null}
                {rows.map((row) => (
                  <ConsumableRowView
                    key={row.id}
                    row={row}
                    products={products}
                    onChangeQty={(q) => {
                      void onChangeQty(row.id, q);
                    }}
                    onRemove={() => {
                      void onRemove(row.id);
                    }}
                  />
                ))}
                <tr>
                  <td className="px-3 py-2">
                    <select
                      value={draft_id}
                      onChange={(e) => setDraftId(e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                    >
                      <option value="">{t('service_consumables.pick_placeholder')}</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.code} — {p.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Input
                      className="ml-auto h-8 w-24 text-right font-mono"
                      value={draft_qty}
                      onChange={(e) => setDraftQty(e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        void onAdd();
                      }}
                      disabled={adding}
                    >
                      <Plus className="h-4 w-4" aria-hidden />
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              {error}
            </div>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

interface ConsumableRowViewProps {
  row: ConsumableRow;
  products: { id: string; code: string; name: string }[];
  onChangeQty: (qty: string) => void;
  onRemove: () => void;
}

function ConsumableRowView({ row, products, onChangeQty, onRemove }: ConsumableRowViewProps): JSX.Element {
  const [qty, setQty] = useState<string>(row.quantity);
  useEffect(() => setQty(row.quantity), [row.quantity]);

  const label = products.find((p) => p.id === row.product_id);

  return (
    <tr>
      <td className="px-3 py-2">
        <span className="font-mono text-xs text-muted-foreground">{label?.code ?? ''}</span>
        <span className="ml-2">{label?.name ?? '—'}</span>
      </td>
      <td className="px-3 py-2 text-right">
        <Input
          className="ml-auto h-8 w-24 text-right font-mono"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          onBlur={() => {
            if (qty !== row.quantity) onChangeQty(qty);
          }}
        />
      </td>
      <td className="px-3 py-2 text-right">
        <Button type="button" size="sm" variant="ghost" onClick={onRemove}>
          <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
        </Button>
      </td>
    </tr>
  );
}
